import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import Swal from 'sweetalert2';
import { ApiResponse } from '@shared/models/api-response.model';
import { PaymentMode } from '@shared/models/payment-mode.model';
import { DepositLimitNew } from '@shared/models/deposit-limit-new.model';

/**
 * The whole API surface of the new deposit flow (api2 DepositController).
 *
 * The legacy deposit endpoints stay in PortalDepositHttpService — the two flows talk to
 * different backend models and only `check-requirement` is shared (used by the deposit guard).
 */
@Injectable({
  providedIn: 'root'
})
export class NewDepositHttpService {

  constructor(private http: HttpClient, private translateService: TranslateService) {}

  getDepositPaymentMode(): Observable<PaymentMode[]> {
    return this.http.get<ApiResponse>(`/deposit/paymentmode?type=1&status=1`).pipe(
      map(res => res.data.rows
        .sort((a: PaymentMode, b: PaymentMode) => (a.position ?? 99) - (b.position ?? 99))
        .map((item: PaymentMode) => ({
          ...item,
          display_name: item.display_name ?? item.name,
          shortcut_amount: item.shortcut_amount?.map((amt: any) => +amt) || [],
          available_payment_options: item.available_payment_options?.sort((a: any, b: any) =>
            (a.position ?? Infinity) - (b.position ?? Infinity) || a.name.localeCompare(b.name)) || []
        })))
    );
  }

  getMemberBankAccounts(): Observable<any[]> {
    return this.http.get<ApiResponse>(`/bankaccount?paginate=false`).pipe(
      map(res => res.data.rows)
    );
  }

  getDepositLimitNew(payment_mode_id: number): Observable<DepositLimitNew> {
    return this.http.get<ApiResponse>(`/deposit/getlimitnew?payment_mode_id=${payment_mode_id}&payment_account_list=true`).pipe(
      map(res => {
        const depositLimit: DepositLimitNew = res.data.rows;
        depositLimit.max = +depositLimit.max;
        depositLimit.min = +depositLimit.min;
        return depositLimit;
      })
    );
  }

  addTwostepDeposit(data: { amount: number; payment_mode_id: number; promotion_id?: number }): Observable<any> {
    return this.http.post<ApiResponse>(`/deposit/depositdirecttransfer`, data).pipe(
      map(res => res.data.rows)
    );
  }

  updateTwostepDeposit(depositId: number, data: { merchant_bank_id: number; receipt_path: string[] }): Observable<any> {
    return this.http.put<ApiResponse>(`/deposit/depositdirecttransfer/${depositId}`, data).pipe(
      map(res => res)
    );
  }

  cancelTwostepDeposit(depositId: number): Observable<any> {
    return this.http.put<ApiResponse>(`/deposit/cancelpendingpayments/${depositId}`, {}).pipe(
      map(res => res)
    );
  }

  changePendingPaymentsDepositToExpired(depositId: number): Observable<any> {
    return this.http.put<ApiResponse>(`/deposit/expiredpendingpayments/${depositId}`, {}).pipe(
      map(res => res)
    );
  }

  getPendingPaymentTwostepDeposit(): Observable<any> {
    return this.http.get<ApiResponse>(`/deposit/getpendingpaymentsnew`).pipe(
      map(res => res)
    );
  }

  getEligiblePaymentGateway(payment_mode_id: number, payment_option_ids?: number[]): Observable<any> {
    const params: string[] = [`payment_mode_id=${payment_mode_id}`];
    if (payment_option_ids?.length) {
      payment_option_ids.forEach((id, index) => {
        params.push(`payment_option_ids[${index}]=${id}`);
      });
    }
    return this.http.get<ApiResponse>(`/deposit/geteligiblepaymentgateway?${params.join('&')}`).pipe(
      map(res => {
        const pg = res.data.rows;
        pg.max = +pg.max;
        pg.min = +pg.min;
        return pg;
      })
    );
  }

  addPaymentGatewayDeposit(data: {
    amount: number;
    payment_mode_id: number;
    payment_gateway_config_id: number;
    payment_processor_id: number;
    payment_option_code: string;
    promotion_id?: number;
  }): Observable<any> {
    return this.http.post<ApiResponse>(`/deposit/depositpaymentgateway`, data).pipe(
      map(res => res)
    );
  }

  getEligiblePaymentAccount(member_account_id: number, payment_mode_id: number, payment_option_id: number): Observable<any> {
    return this.http.get<ApiResponse>(`/paymentaccount/geteligiblepaymentaccount?member_account_id=${member_account_id}&payment_mode_id=${payment_mode_id}&payment_option_id=${payment_option_id}`).pipe(
      map(res => res.data)
    );
  }

  storeNew(data: {
    amount: number;
    payment_mode_id: number;
    payment_account_id: number;
    receipt_path?: string[];
    promotion_id?: number;
  }): Observable<any> {
    return this.http.post<ApiResponse>(`/deposit/storenew`, data).pipe(
      map(res => res)
    );
  }

  downloadQRCode(url: string): void {
    const ext = url.split('.').pop();
    const ts = new Date().getTime();
    const src = '//' + url.replace(/^https?:\/\//i, '');
    const headers = new HttpHeaders({ 'Cache-Control': 'no-cache' });

    this.http.get(src, { headers, responseType: 'blob' }).subscribe({
      next: blob => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${ts}.${ext}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      },
      error: error => {
        console.error('Error downloading the QR code: ', error);
        Swal.fire({ html: this.translateService.instant('The QR code could not be downloaded. Please try again.') });
      }
    });
  }
}
